import * as React from 'react';

import { requestPost, } from '../../../modules/request';
import { Checkbox, DataTable, EXTRA_SMALL_ICON_BASE_SRC, ButtonSpinner, Snackbar, Spinner, Tooltip, defaultSnackbarTimeout, filterIcon, helpIcon, noResultsMessage } from '../../Shared';
import useSnackbarMonitor from '../../Shared/hooks/useSnackbarMonitor';
import { hashPayload, } from '../shared';

const STANDARD_WIDTH = "176px";
// wider columns leave room for the sort arrow next to the longer metric headers
const WIDE_WIDTH = "204px";
const TEACHER_NAME = "Teacher Name";
const TEACHER_EMAIL = "Teacher Email";
const SCHOOL = "School";
const TOTAL_ACTIVE_STUDENTS = "Total Active Students";
const TOTAL_ACTIVITIES_ASSIGNED = "Total Activities Assigned";
const TOTAL_ACTIVITIES_COMPLETED = "Total Activities Completed";
const TOTAL_BASELINE_DIAGNOSTICS_COMPLETED = "Total Baseline Diagnostics Completed";
const TOTAL_GROWTH_DIAGNOSTICS_COMPLETED = "Total Growth Diagnostics Completed";
const AVERAGE_ACTIVITIES_COMPLETED_PER_STUDENT = "Average Activities Completed Per Student";
const AVERAGE_ACTIVITIES_PER_STUDENT = "Average Activities Per Student";
const ROLE = "Role";

const PUSHER_EVENT_KEY = "data-export-cached";
// polling is a fallback in case the Pusher notification that the report is ready never arrives; the last interval repeats
const POLLING_INTERVALS_MS = [5000, 10000, 20000, 30000]
const MAX_POLLING_ATTEMPTS = 10
const DOWNLOAD_SUCCESS_TEXT = "You will receive an email with a download link shortly."
const DOWNLOAD_ERROR_TEXT = "Something went wrong generating your download. Please try again."

interface TeacherUsageTableAndFieldsProps {
  customTimeframeEnd: string;
  customTimeframeStart: string;
  horizontalFilters?: React.ReactNode;
  openMobileFilterMenu: Function;
  pusherChannel?: any;
  queryKey: string;
  searchCount: number;
  selectedClassroomIds: number[];
  selectedGrades: string[];
  selectedSchoolIds: number[];
  selectedTeacherIds: number[];
  selectedTimeframe: string;
}

export const TeacherUsageTableAndFields = ({ queryKey, searchCount, selectedGrades, selectedSchoolIds, selectedTeacherIds, selectedClassroomIds, selectedTimeframe, customTimeframeStart, customTimeframeEnd, openMobileFilterMenu, pusherChannel, horizontalFilters }: TeacherUsageTableAndFieldsProps) => {
  const [showTeacherEmail, setShowTeacherEmail] = React.useState<boolean>(true);
  const [showSchool, setShowSchool] = React.useState<boolean>(true);
  const [showTotalActiveStudents, setShowTotalActiveStudents] = React.useState<boolean>(true);
  const [showTotalActivitiesAssigned, setShowTotalActivitiesAssigned] = React.useState<boolean>(true);
  const [showTotalActivitiesCompleted, setShowTotalActivitiesCompleted] = React.useState<boolean>(true);
  const [showTotalBaselineDiagnosticsCompleted, setShowTotalBaselineDiagnosticsCompleted] = React.useState<boolean>(true);
  const [showTotalGrowthDiagnosticsCompleted, setShowTotalGrowthDiagnosticsCompleted] = React.useState<boolean>(true);
  const [showAverageActivitiesPerStudent, setShowAverageActivitiesPerStudent] = React.useState<boolean>(true);
  const [showRole, setShowRole] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [generationError, setGenerationError] = React.useState<boolean>(false);
  const [downloadButtonBusy, setDownloadButtonBusy] = React.useState<boolean>(false);
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarText, setSnackbarText] = React.useState<string>(DOWNLOAD_SUCCESS_TEXT);
  const [data, setData] = React.useState<any>(null);
  const [pusherMessage, setPusherMessage] = React.useState<any>(null)
  const [customTimeframeStartString, setCustomTimeframeStartString] = React.useState(null)
  const [customTimeframeEndString, setCustomTimeframeEndString] = React.useState(null)
  const pollingTimeoutRef = React.useRef(null)
  const pollingAttemptsRef = React.useRef(0)
  const fetchIdRef = React.useRef(0)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const fields = {
    [TEACHER_NAME]: {
      dataTableField: { name: TEACHER_NAME, attribute: "teacher_name", width: STANDARD_WIDTH, renderCellsAsHeaders: true, isSortable: true },
      checked: true
    },
    [TEACHER_EMAIL]: {
      dataTableField: { name: TEACHER_EMAIL, attribute: "teacher_email", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowTeacherEmail,
      checked: showTeacherEmail
    },
    [SCHOOL]: {
      dataTableField: { name: SCHOOL, attribute: "school_name", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowSchool,
      checked: showSchool
    },
    [TOTAL_ACTIVE_STUDENTS]: {
      dataTableField: { name: TOTAL_ACTIVE_STUDENTS, attribute: "total_active_students", width: "192px", isSortable: true },
      setterFunction: setShowTotalActiveStudents,
      checked: showTotalActiveStudents
    },
    [TOTAL_ACTIVITIES_ASSIGNED]: {
      dataTableField: { name: TOTAL_ACTIVITIES_ASSIGNED, attribute: "total_activities_assigned", width: "216px", isSortable: true },
      setterFunction: setShowTotalActivitiesAssigned,
      checked: showTotalActivitiesAssigned
    },
    [TOTAL_ACTIVITIES_COMPLETED]: {
      dataTableField: { name: TOTAL_ACTIVITIES_COMPLETED, attribute: "total_activities_completed", width: "216px", isSortable: true },
      setterFunction: setShowTotalActivitiesCompleted,
      checked: showTotalActivitiesCompleted
    },
    [TOTAL_BASELINE_DIAGNOSTICS_COMPLETED]: {
      dataTableField: { name: TOTAL_BASELINE_DIAGNOSTICS_COMPLETED, attribute: "total_baseline_diagnostics_completed", width: WIDE_WIDTH, isSortable: true },
      setterFunction: setShowTotalBaselineDiagnosticsCompleted,
      checked: showTotalBaselineDiagnosticsCompleted
    },
    [TOTAL_GROWTH_DIAGNOSTICS_COMPLETED]: {
      dataTableField: { name: TOTAL_GROWTH_DIAGNOSTICS_COMPLETED, attribute: "total_growth_diagnostics_completed", width: WIDE_WIDTH, isSortable: true },
      setterFunction: setShowTotalGrowthDiagnosticsCompleted,
      checked: showTotalGrowthDiagnosticsCompleted
    },
    [AVERAGE_ACTIVITIES_COMPLETED_PER_STUDENT]: {
      dataTableField: { name: AVERAGE_ACTIVITIES_PER_STUDENT, attribute: "average_activities_per_student", width: WIDE_WIDTH, isSortable: true },
      setterFunction: setShowAverageActivitiesPerStudent,
      checked: showAverageActivitiesPerStudent
    },
    [ROLE]: {
      dataTableField: { name: ROLE, attribute: "role", width: STANDARD_WIDTH, isSortable: true },
      setterFunction: setShowRole,
      checked: showRole
    },
  };

  React.useEffect(() => {
    if (!pusherChannel) { return }

    const handlePusherMessage = (body) => setPusherMessage(body.message)
    pusherChannel.bind(PUSHER_EVENT_KEY, handlePusherMessage)

    // Unbind on cleanup: the channel outlives this component while navigating between reports,
    // so an anonymous handler would accumulate stale callbacks across visits.
    return () => pusherChannel.unbind(PUSHER_EVENT_KEY, handlePusherMessage)
  }, [pusherChannel])

  React.useEffect(() => {
    return () => {
      // Invalidate in-flight fetches so a late response can't schedule polling or setState after unmount
      fetchIdRef.current += 1
      clearTimeout(pollingTimeoutRef.current)
    }
  }, [])

  React.useEffect(() => {
    getData()
  }, [searchCount])

  React.useEffect(() => {
    if (!customTimeframeStart) return

    setCustomTimeframeStartString(customTimeframeStart.toISOString())
  }, [customTimeframeStart])

  React.useEffect(() => {
    if (!customTimeframeEnd) return setCustomTimeframeEndString(null)

    setCustomTimeframeEndString(customTimeframeEnd.toISOString())
  }, [customTimeframeEnd])

  React.useEffect(() => {
    if (!pusherMessage) return

    if (filtersMatchHash(pusherMessage)) getData()
  }, [pusherMessage])

  function getData() {
    pollingAttemptsRef.current = 0
    fetchIdRef.current += 1
    setGenerationError(false)
    setLoading(true)
    fetchData(fetchIdRef.current)
  }

  function fetchData(fetchId: number) {
    // a stale fetchId means new filters have superseded this polling chain; the current chain owns the timeout
    if (fetchId !== fetchIdRef.current) { return }

    clearTimeout(pollingTimeoutRef.current)

    const searchParams = {
      query: queryKey,
      timeframe: selectedTimeframe,
      timeframe_custom_start: customTimeframeStart,
      timeframe_custom_end: customTimeframeEnd,
      school_ids: selectedSchoolIds,
      teacher_ids: selectedTeacherIds,
      classroom_ids: selectedClassroomIds,
      grades: selectedGrades
    }

    requestPost('/snapshots/data_export', searchParams, (body) => {
      if (fetchId !== fetchIdRef.current) { return }

      if (!body.hasOwnProperty('results')) {
        setLoading(true)
        schedulePollingAttempt(fetchId)
      } else {
        const { results, } = body
        // We consider `null` to be a lack of data, so if the result is `[]` we need to explicitly `setData(null)`
        const data = results.length > 0 ? results : null
        const formattedData = formatData(data)
        setData(formattedData)
        setLoading(false)
      }
    }, () => handleGenerationFailure(fetchId)).catch(() => handleGenerationFailure(fetchId))
  }

  function handleGenerationFailure(fetchId: number) {
    // Non-2xx responses (error callback) and network failures (rejected promise) never hit the
    // success path, so surface the terminal error state instead of spinning forever
    if (fetchId !== fetchIdRef.current) { return }

    clearTimeout(pollingTimeoutRef.current)
    setLoading(false)
    setGenerationError(true)
  }

  function schedulePollingAttempt(fetchId: number) {
    // Give up after the final attempt with an explicit terminal state so the preview never spins forever
    if (pollingAttemptsRef.current >= MAX_POLLING_ATTEMPTS) {
      setLoading(false)
      setGenerationError(true)
      return
    }

    const interval = POLLING_INTERVALS_MS[Math.min(pollingAttemptsRef.current, POLLING_INTERVALS_MS.length - 1)]
    pollingAttemptsRef.current += 1
    pollingTimeoutRef.current = setTimeout(() => fetchData(fetchId), interval)
  }

  function createCsvReportDownload() {
    // guard against duplicate exports from repeated clicks while a request is in flight
    if (downloadButtonBusy) { return }

    const buttonDisableTime = 2000
    const requestParams = {
      query: queryKey,
      timeframe: selectedTimeframe,
      timeframe_custom_start: customTimeframeStart,
      timeframe_custom_end: customTimeframeEnd,
      school_ids: selectedSchoolIds,
      teacher_ids: selectedTeacherIds,
      classroom_ids: selectedClassroomIds,
      grades: selectedGrades,
      headers_to_display: getHeaders().map(header => header.attribute)
    }
    setDownloadButtonBusy(true)

    const handleDownloadFailure = () => {
      // Re-enable the button and surface feedback on any error path so it isn't stuck disabled
      setSnackbarText(DOWNLOAD_ERROR_TEXT)
      setShowSnackbar(true)
      setDownloadButtonBusy(false)
    }

    requestPost('/snapshots/create_csv_report_download', requestParams, (body) => {
      setSnackbarText(DOWNLOAD_SUCCESS_TEXT)
      setShowSnackbar(true)
      setTimeout(() => { setDownloadButtonBusy(false) }, buttonDisableTime);
    }, handleDownloadFailure).catch(handleDownloadFailure)
  }

  function filtersMatchHash(hashMessage) {
    const filterTarget = [].concat(
      queryKey,
      selectedTimeframe,
      customTimeframeStartString,
      customTimeframeEndString,
      selectedSchoolIds,
      selectedGrades,
      selectedTeacherIds,
      selectedClassroomIds
    )

    const filterHash = hashPayload(filterTarget)

    return hashMessage === filterHash
  }

  function formatData(data) {
    if (!data) { return null }

    return data.map((entry, index) => ({ ...entry, id: index }))
  }

  function toggleCheckbox(e) {
    e.preventDefault();
    const fieldLabel = e.currentTarget.id;
    const checked = fields[fieldLabel].checked;
    const setterFunction = fields[fieldLabel].setterFunction
    setterFunction(!checked);
  }

  function renderCheckboxes() {
    return Object.keys(fields).map((fieldLabel, i) => {
      // skip Teacher Name because we want this field to always be present
      if (i === 0) { return }

      return (
        <Checkbox
          key={fieldLabel}
          label={fieldLabel}
          onClick={toggleCheckbox}
          selected={fields[fieldLabel].checked}
        />
      )
    })
  }

  function getHeaders() {
    return Object.keys(fields).flatMap(fieldLabel => {
      const headerSelected = fields[fieldLabel].checked
      if (!headerSelected) { return [] }
      return fields[fieldLabel].dataTableField;
    })
  }

  return (
    <React.Fragment>
      <div className="header">
        <Snackbar text={snackbarText} visible={showSnackbar} />
        <h1>
          Teacher Usage Export
        </h1>
        <div className="header-buttons">
          <button className="quill-button outlined small grey focus-on-light" disabled={downloadButtonBusy} onClick={createCsvReportDownload} type="button">
            {downloadButtonBusy ? <ButtonSpinner variant="dark" /> : <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`} />}
            <span>Download</span>
          </button>
        </div>
      </div>
      <div className="filter-button-container">
        <button className="interactive-wrapper focus-on-light" onClick={openMobileFilterMenu} type="button">
          <img alt={filterIcon.alt} src={filterIcon.src} />
          Filters
        </button>
      </div>
      {horizontalFilters}
      <div className="teacher-usage-export-container data-export-container">
        <section className="fields-section">
          <h2>Fields</h2>
          <div className="fields-container">
            {renderCheckboxes()}
          </div>
        </section>
        <section className="preview-section">
          <h2>Preview</h2>
          <Tooltip
            tooltipText="This preview is limited to the first 10 results. Your download will include all teachers matching your criteria."
            tooltipTriggerText={<img alt={helpIcon.alt} src={helpIcon.src} />}
          />
        </section>
        {loading && <Spinner />}
        {!loading && generationError && (
          <div className="generation-error">
            <p>This report is taking longer than expected to generate. Please try again.</p>
            <button className="quill-button outlined small grey focus-on-light" onClick={getData} type="button">
              Try again
            </button>
          </div>
        )}
        {!loading && !generationError && <DataTable
          className="data-export-table"
          defaultSortAttribute="teacher_name"
          defaultSortDirection="ascending"
          emptyStateMessage={noResultsMessage('teacher')}
          headers={getHeaders()}
          reportingFormat={true}
          rows={data || []}
        />}
      </div>
    </React.Fragment>
  )
}

export default TeacherUsageTableAndFields;
